import { useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Button, F0Text } from '@factorialco/f0-react'
import { Delete } from '@factorialco/f0-react/icons/app'
import { runInAction } from 'mobx'
import { observer } from 'mobx-react'

import i18n from 'modules/core/lib/i18n'

import 'modules/trainings/components/content/index.styles.css'
import 'modules/trainings/components/content/items/shared/index.styles.css'
import { InlineEditableTitle } from 'modules/trainings/components/content/items/shared/fields/InlineEditableTitle'
import { ContentRenderProps } from 'modules/trainings/components/content/types'
import { getContrastTextColor } from 'modules/trainings/lib/content/colorUtils'
import { ContentModule, FULLSCREEN_SCALE } from 'modules/trainings/lib/content/Module'

import { EditDock } from './EditDock/EditDock'

import './index.styles.css'

const ModuleView = observer(
  ({
    contentItem,
    readOnly = false,
    onContentChange,
    isFullscreen = false,
  }: ContentRenderProps<ContentModule>) => {
    const { id: trainingId } = useParams()

    const position = isFullscreen ? 'fixed' : undefined
    const containerClassName = isFullscreen
      ? 'module_moduleContainerFullscreen'
      : 'module_moduleContainerPreview'
    const innerPaddingX = isFullscreen ? undefined : 's32'
    const textMaxWidth = isFullscreen ? undefined : 's600'
    const innerClassName = isFullscreen ? 'content_contentContainer' : ''

    const textStyle = {
      color: getContrastTextColor(contentItem.backgroundColor),
      fontSize: isFullscreen
        ? FULLSCREEN_SCALE[contentItem.fontSize] || contentItem.fontSize
        : contentItem.fontSize,
      fontWeight: Number(contentItem.fontWeight) || 600,
      lineHeight: 1.2,
      textAlign: 'left' as const,
    }

    const backgroundImageUrl = contentItem.thumbnail?.url
    const hasBackgroundImage = !!backgroundImageUrl

    const handleTitleChange = (value: string) => {
      contentItem.title = value
      onContentChange?.()
    }

    const handleRemoveImage = () => {
      runInAction(() => {
        contentItem.thumbnail = undefined
      })
      onContentChange?.()
    }

    const containerStyle = {
      backgroundColor: contentItem.backgroundColor,
      ...(hasBackgroundImage
        ? {
            backgroundImage: `url(${backgroundImageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }
        : {}),
    }

    const moduleBox = (
      <Box
        width="full"
        height="full"
        alignItems="center"
        justifyContent="center"
        position={position}
        className={containerClassName}
        style={containerStyle}
      >
        {hasBackgroundImage && <div className="module_overlay" />}

        {hasBackgroundImage && !readOnly && (
          <Box className="shared_mediaOverlay" background="grey400">
            <F0Button
              variant="neutral"
              size="md"
              onClick={handleRemoveImage}
              icon={Delete}
              label={i18n.t('trainings.content.edit_dock.remove_background_image')}
              hideLabel
            />
          </Box>
        )}

        <Box
          width="full"
          paddingX={innerPaddingX}
          className={`${innerClassName} module_contentInner`}
        >
          <Box maxWidth={textMaxWidth} className="module_centerMargin">
            {readOnly ? (
              <F0Text content={contentItem.title} variant="body" style={textStyle} />
            ) : (
              <InlineEditableTitle
                value={contentItem.title}
                placeholder={i18n.t('trainings.content.module_modal.label_placeholder')}
                ariaLabel={i18n.t('trainings.content.module_modal.label_title')}
                style={textStyle}
                onChange={handleTitleChange}
              />
            )}
          </Box>
        </Box>
      </Box>
    )

    if (isFullscreen) {
      return (
        <div className="module_fullscreenWrapper">
          {moduleBox}
          {!readOnly && (
            <EditDock
              contentItem={contentItem}
              onContentChange={onContentChange}
              trainingId={String(trainingId)}
            />
          )}
        </div>
      )
    }

    return (
      <Box width="full" height="full" paddingTop="s16" paddingX="s64" paddingBottom="s64">
        <div className="module_previewWrapper">
          {moduleBox}
          {!readOnly && (
            <EditDock
              contentItem={contentItem}
              onContentChange={onContentChange}
              trainingId={String(trainingId)}
            />
          )}
        </div>
      </Box>
    )
  }
)

export { ModuleView }
